// Painel TV — dashboard fullscreen em tempo real pra ficar aberto numa
// TV do escritório. Auto-refresh dos pedidos a cada 30s, layout grande
// pra leitura a ~3-5m de distância (TV 50"+).
//
// Acesso: rota /painel-tv. Faz login uma vez com a conta do MINI Deck e
// fica aberto indefinidamente (sessão Supabase persiste).
//
// Conversões (taxa de fechamento) ficam como placeholder pra integração
// futura com a Vendedora IA Mila (WhatsApp).

import { useEffect, useState, useMemo, useRef } from 'react'
import { getPedidos } from '../utils/storage'
import { syncFromSupabase } from '../utils/supabase'
import { KANBAN_COLUNAS } from '../data/kanban'
import { format, formatDistanceToNow, isToday, isSameMonth, isSameYear } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { fmtMoeda } from '../utils/fmt'
import { TrendingUp, Truck, Trophy, Clock, Sparkles, Activity, MessageCircle } from 'lucide-react'

// Intervalo de atualização do painel (ms). 30s evita sobrecarga e ainda
// é "real-time" o suficiente pra TV de escritório.
const REFRESH_MS = 30_000

// Dia/Mês corrente — recalculados em cada render porque a TV fica aberta
// dias e o "hoje" muda ao passar da meia-noite.
function hoje() { return new Date() }

// Atalho pra exibir número de cards no centro
function NumeroBig({ children, cor = '#F5C200' }) {
  return (
    <p className="text-6xl 2xl:text-7xl font-black tabular-nums tracking-tight" style={{ color: cor }}>
      {children}
    </p>
  )
}

function Card({ titulo, icone: Icon, cor = '#F5C200', children, className = '' }) {
  return (
    <div className={`bg-[#1a1a1a] border border-white/5 rounded-2xl p-6 flex flex-col ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={18} style={{ color: cor }} />}
        <h2 className="text-xs font-bold uppercase tracking-widest text-white/50">{titulo}</h2>
      </div>
      {children}
    </div>
  )
}

// Card de ranking horizontal — usado pra top vendedoras e top modelos.
// Mostra até N linhas com barra de progresso proporcional ao maior valor.
function Ranking({ itens, getLabel, getValor, getSecondary, cor = '#F5C200' }) {
  const max = Math.max(...itens.map(getValor), 1)
  return (
    <div className="space-y-2 flex-1">
      {itens.map((it, i) => {
        const v = getValor(it)
        const pct = (v / max) * 100
        return (
          <div key={i} className="relative">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-white font-semibold truncate flex items-center gap-2">
                <span className="text-[10px] font-mono text-white/40 w-4">#{i + 1}</span>
                {getLabel(it)}
              </span>
              <span className="font-bold tabular-nums" style={{ color: cor }}>
                {getSecondary ? getSecondary(it) : v}
              </span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: cor }}
              />
            </div>
          </div>
        )
      })}
      {itens.length === 0 && (
        <p className="text-white/30 text-sm italic">Sem dados ainda</p>
      )}
    </div>
  )
}

// Relógio digital grande — atualiza a cada segundo pra sensação de "ao vivo"
function Relogio() {
  const [agora, setAgora] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="text-right">
      <p className="text-3xl 2xl:text-4xl font-black tabular-nums text-white">
        {format(agora, 'HH:mm:ss')}
      </p>
      <p className="text-xs uppercase tracking-widest text-white/40 mt-0.5">
        {format(agora, "EEEE, dd 'de' MMMM", { locale: ptBR })}
      </p>
    </div>
  )
}

// Pulso "ao vivo" — animação verde discreta indicando que os dados
// estão sendo atualizados periodicamente
function PulsoLive() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      Ao vivo
    </span>
  )
}

export function PainelTV() {
  const [pedidos, setPedidos] = useState(() => getPedidos())
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(new Date())
  const syncingRef = useRef(false)

  // Refresh: sincroniza com Supabase + relê do localStorage
  async function atualizar() {
    if (syncingRef.current) return
    syncingRef.current = true
    try {
      await syncFromSupabase()
      setPedidos(getPedidos())
      setUltimaAtualizacao(new Date())
    } catch (_) {
      // Silencia falhas de rede — o painel continua mostrando o último dado
    } finally {
      syncingRef.current = false
    }
  }

  // Auto-refresh
  useEffect(() => {
    atualizar()
    const t = setInterval(atualizar, REFRESH_MS)
    return () => clearInterval(t)
  }, [])

  // Pedidos REAIS (sem AJ, sem filhos divididos — só vendas de verdade)
  const pedidosReais = useMemo(
    () => pedidos.filter(p => !p.tipoAjuste && !p.dividido),
    [pedidos]
  )

  const dataRef = hoje()

  // ── KPIs do dia ──────────────────────────────────────────────────────
  const pedidosHoje = useMemo(() => pedidosReais.filter(p => {
    const d = p.dataVenda || (p.criadoEm || '').split('T')[0]
    if (!d) return false
    try { return isToday(new Date(d + 'T12:00:00')) } catch { return false }
  }), [pedidosReais])

  const vendasHoje = pedidosHoje.reduce((s, p) => s + Number(p.valorTotal || 0), 0)

  // ── KPIs do mês ──────────────────────────────────────────────────────
  const pedidosMes = useMemo(() => pedidosReais.filter(p => {
    const d = p.dataVenda || (p.criadoEm || '').split('T')[0]
    if (!d) return false
    try {
      const data = new Date(d + 'T12:00:00')
      return isSameMonth(data, dataRef) && isSameYear(data, dataRef)
    } catch { return false }
  }), [pedidosReais, dataRef])

  const vendasMes = pedidosMes.reduce((s, p) => s + Number(p.valorTotal || 0), 0)
  const ticketMes = pedidosMes.length > 0 ? vendasMes / pedidosMes.length : 0

  // ── Última venda ─────────────────────────────────────────────────────
  const ultimaVenda = useMemo(() => {
    const ordenados = [...pedidosReais].sort((a, b) => {
      const da = a.criadoEm || a.dataVenda || ''
      const db = b.criadoEm || b.dataVenda || ''
      return db.localeCompare(da)
    })
    return ordenados[0] || null
  }, [pedidosReais])

  // ── Top Vendedoras (mês) ─────────────────────────────────────────────
  const topVendedoras = useMemo(() => {
    const map = {}
    pedidosMes.forEach(p => {
      const v = (p.vendedora || 'Sem vendedora').trim() || 'Sem vendedora'
      if (!map[v]) map[v] = { nome: v, total: 0, qtd: 0 }
      map[v].total += Number(p.valorTotal || 0)
      map[v].qtd++
    })
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 5)
  }, [pedidosMes])

  // ── Top Modelos (mês) ────────────────────────────────────────────────
  const topModelos = useMemo(() => {
    const map = {}
    pedidosMes.forEach(p => {
      const ms = Array.isArray(p.moveis) ? p.moveis : []
      ms.forEach(m => {
        const nome = (m?.modelo || '').trim()
        if (!nome) return
        if (!map[nome]) map[nome] = { modelo: nome, qtd: 0 }
        map[nome].qtd++
      })
    })
    return Object.values(map).sort((a, b) => b.qtd - a.qtd).slice(0, 5)
  }, [pedidosMes])

  // ── Próximas entregas (próximos 7 dias) ──────────────────────────────
  const proximasEntregas = useMemo(() => {
    const hojeYmd = format(dataRef, 'yyyy-MM-dd')
    const limite  = format(new Date(dataRef.getTime() + 7 * 86400000), 'yyyy-MM-dd')
    return pedidosReais
      .filter(p => p.prazo && p.prazo >= hojeYmd && p.prazo <= limite
        && p.status !== 'ENTREGUE' && p.status !== 'POS_VENDA')
      .sort((a, b) => a.prazo.localeCompare(b.prazo))
      .slice(0, 6)
  }, [pedidosReais, dataRef])

  // ── Pedidos por status (produção) ────────────────────────────────────
  const porStatus = useMemo(() => {
    const map = {}
    KANBAN_COLUNAS.forEach(c => { map[c.id] = 0 })
    pedidosReais.forEach(p => {
      if (p.status === 'ENTREGUE' || p.status === 'POS_VENDA') return
      if (map[p.status] !== undefined) map[p.status]++
    })
    return KANBAN_COLUNAS.filter(c => c.id !== 'ENTREGUE').map(c => ({
      label: c.label,
      qtd:   map[c.id] || 0,
    }))
  }, [pedidosReais])

  const tempoUltimaVenda = ultimaVenda?.criadoEm
    ? formatDistanceToNow(new Date(ultimaVenda.criadoEm), { locale: ptBR, addSuffix: true })
    : null

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] text-white overflow-hidden p-6 2xl:p-8">

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <img src="/logo.png" alt="" className="w-12 h-12 rounded-xl" onError={e => e.target.style.display = 'none'} />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl 2xl:text-3xl font-black tracking-tight">MINI Deck</h1>
              <PulsoLive />
            </div>
            <p className="text-xs uppercase tracking-widest text-white/40 mt-0.5">
              Painel de vendas · atualizado {formatDistanceToNow(ultimaAtualizacao, { locale: ptBR, addSuffix: true })}
            </p>
          </div>
        </div>
        <Relogio />
      </div>

      {/* ── Grid principal ────────────────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-4 2xl:gap-6 h-[calc(100vh-130px)]">

        {/* HOJE — destaque */}
        <Card titulo="Vendas hoje" icone={Sparkles} className="col-span-4">
          <NumeroBig>{pedidosHoje.length}</NumeroBig>
          <p className="text-xs uppercase tracking-widest text-white/40 mt-1">
            {pedidosHoje.length === 1 ? 'pedido' : 'pedidos'}
          </p>
          <p className="text-2xl 2xl:text-3xl font-bold text-white mt-3 tabular-nums">
            {fmtMoeda(vendasHoje)}
          </p>
        </Card>

        {/* MÊS */}
        <Card titulo={`${format(dataRef, 'MMMM', { locale: ptBR })} ${dataRef.getFullYear()}`} icone={TrendingUp} className="col-span-4">
          <NumeroBig cor="#FFFFFF">{pedidosMes.length}</NumeroBig>
          <p className="text-xs uppercase tracking-widest text-white/40 mt-1">pedidos no mês</p>
          <p className="text-2xl 2xl:text-3xl font-bold text-[#F5C200] mt-3 tabular-nums">
            {fmtMoeda(vendasMes)}
          </p>
        </Card>

        {/* TICKET MÉDIO */}
        <Card titulo="Ticket médio do mês" icone={Activity} className="col-span-4">
          <NumeroBig cor="#10B981">{fmtMoeda(ticketMes)}</NumeroBig>
          {ultimaVenda && (
            <div className="mt-auto pt-3 border-t border-white/5">
              <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5 flex items-center gap-1.5">
                <Clock size={11} />
                Última venda · {tempoUltimaVenda}
              </p>
              <p className="text-sm font-semibold text-white truncate">
                {ultimaVenda.cliente?.nome || '—'}
              </p>
              <p className="text-xs text-white/50 truncate">
                #{ultimaVenda.numero} · {fmtMoeda(ultimaVenda.valorTotal || 0)} · {ultimaVenda.vendedora || 'sem vendedora'}
              </p>
            </div>
          )}
        </Card>

        {/* TOP VENDEDORAS */}
        <Card titulo="Top vendedoras (mês)" icone={Trophy} className="col-span-4">
          <Ranking
            itens={topVendedoras}
            getLabel={v => v.nome}
            getValor={v => v.total}
            getSecondary={v => fmtMoeda(v.total)}
            cor="#F5C200"
          />
        </Card>

        {/* TOP MODELOS */}
        <Card titulo="Top modelos (mês)" className="col-span-4">
          <Ranking
            itens={topModelos}
            getLabel={m => m.modelo}
            getValor={m => m.qtd}
            getSecondary={m => `${m.qtd}×`}
            cor="#A78BFA"
          />
        </Card>

        {/* CONVERSÕES — placeholder pra integrar com Vendedora IA Mila */}
        <Card titulo="Conversões" icone={MessageCircle} className="col-span-4" cor="#10B981">
          <div className="flex flex-col items-center justify-center flex-1 text-center">
            <MessageCircle size={32} className="text-white/20 mb-3" />
            <p className="text-sm font-semibold text-white/60 mb-1">Em breve</p>
            <p className="text-xs text-white/40 leading-relaxed max-w-[200px]">
              Integração com Vendedora IA Mila no WhatsApp pra mostrar
              <strong className="text-white/60"> conversas iniciadas → fechamentos</strong> em tempo real
            </p>
          </div>
        </Card>

        {/* PRÓXIMAS ENTREGAS */}
        <Card titulo="Próximas entregas" icone={Truck} className="col-span-6">
          <div className="space-y-2 flex-1">
            {proximasEntregas.map(p => {
              const dataPrazo = new Date(p.prazo + 'T12:00:00')
              const ehHoje = isToday(dataPrazo)
              return (
                <div key={p.id} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                  <div className={`w-14 text-center shrink-0 ${ehHoje ? 'text-[#F5C200]' : 'text-white/70'}`}>
                    <p className="text-lg font-black tabular-nums leading-none">{format(dataPrazo, 'dd')}</p>
                    <p className="text-[9px] uppercase tracking-widest leading-tight">{format(dataPrazo, 'MMM', { locale: ptBR })}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{p.cliente?.nome || '—'}</p>
                    <p className="text-xs text-white/40 truncate">
                      #{p.numero} · {p.cliente?.bairro || p.cliente?.cidade || ''}
                    </p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded
                    ${ehHoje ? 'bg-[#F5C200]/20 text-[#F5C200]' : 'bg-white/10 text-white/60'}`}>
                    {ehHoje ? 'Hoje' : p.horarioEntrega || '—'}
                  </span>
                </div>
              )
            })}
            {proximasEntregas.length === 0 && (
              <p className="text-white/30 text-sm italic">Sem entregas nos próximos 7 dias</p>
            )}
          </div>
        </Card>

        {/* PRODUÇÃO POR STATUS */}
        <Card titulo="Produção agora" className="col-span-6">
          <div className="grid grid-cols-5 gap-2 flex-1">
            {porStatus.map(s => (
              <div key={s.label} className="bg-white/5 rounded-xl p-3 flex flex-col items-center justify-center text-center">
                <p className="text-3xl 2xl:text-4xl font-black tabular-nums text-white">{s.qtd}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </Card>

      </div>
    </div>
  )
}
